import React, { useEffect, useMemo, useRef, useState } from "react";
import "./style.scss";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import LogoColumn from "../../../assets/LogoColumn.svg";
import { ReactComponent as Home } from "../../../assets/navigation/Home.svg";
import { ReactComponent as Currencies } from "../../../assets/navigation/Currencies.svg";
import { ReactComponent as Discover } from "../../../assets/navigation/Discover.svg";
import { ReactComponent as Market } from "../../../assets/navigation/Market.svg";
import { ReactComponent as Portfolio } from "../../../assets/navigation/Portfolio.svg";
import ChatNew from "../../../assets/ChatNew.svg";
import ChatNewActive from "../../../assets/ChatNewActive.svg";
import TransactionsHistory from "../../../assets/TransactionsHistory.svg";
import TransactionsHistoryActive from "../../../assets/TransactionsHistoryActive.svg";
import MaleUser from "../../../assets/MaleUser.svg";
import FemaleUser from "../../../assets/FemaleUser.svg";
import BusinessUser from "../../../assets/BusinessUser.svg";
import GoBack from "../../../assets/GoBackApp.svg";
import LogoVelar from "../../../assets/velar/Icon.svg";
import { PATH_LIST } from "shared/lib/react-router";
import Modal from "widgets/auth/modal";
import { useStore } from "zustand";
import { sessionModel } from "entities/session";
import { useBlockScrollbar } from "hooks/useBlockScrollbar";
import { marketStore } from "entities/market";
import { dashboardStore } from "entities/dashboard";
import { ContactsModal } from "pages/app/components/contacts-modal";
import NotificationIcon from "pages/app/components/notification-icon";
import {
  generalSocketStore,
  useUnreadMessagesCount,
} from "services/general.socket";
import { useTranslation } from "react-i18next";
import { sessionStore } from "entities/session/model/sessionModel";
import { AssetType, EntityType, IsVelarApp, SvgIcon } from "static/types";
import { accountStore } from "entities/account";

import { firebaseLogEvent } from "app/libs/firebase";
import { usePageView } from "hooks/usePageView";
import { sircapApi } from "shared/api/sircap";
import { useVerificationNavigation } from "hooks/useNavigateVerification";
import { Capacitor } from "@capacitor/core";
import { getCdnUrl } from "helpers/functions";
import {
  getHolidayNotificationLanguage,
  HOLIDAY_SERVICE_ROWS,
  resolveHolidayNotificationDate,
} from "helpers/holiday-notification";
import useExchangesTimeFrames from "hooks/useExchangesTimeFrames";
import { ioRTStore } from "services/rt.socket";

export const AppLayout: React.FC = () => {
  const { user, entity } = useStore(sessionStore);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("app");
  const { t: o } = useTranslation("onboarding-auth");
  const [previousPath, setPreviousPath] = useState<string | null>(null);
  const { setPrevPathDeal } = useStore(dashboardStore);

  // * todo check an maybe delete if this is not used
  const previousPathRef = useRef<string | null>(null);
  const [currentPath, setCurrentPath] = useState(location.pathname);

  useEffect(() => {
    if (currentPath !== location.pathname) {
      previousPathRef.current = currentPath;
      setCurrentPath(location.pathname);
    }
  }, [location.pathname, currentPath]);
  // *

  usePageView();

  useEffect(() => {
    if (user?.language) {
      if (user.language !== i18n.language) {
        i18n.changeLanguage(user.language);
        const html = document.getElementsByTagName("html");
        html[0].setAttribute("lang", user.language);
      }
    }
  }, [user?.language, i18n?.language]);

  useEffect(() => {
    setPreviousPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (previousPath?.includes("/buy") || previousPath?.includes("/sell")) {
      setPrevPathDeal(true);
    } else {
      setPrevPathDeal(false);
    }
  }, [previousPath]);

  const activePage = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith(PATH_LIST.home)) return "Home";
    if (p.startsWith(PATH_LIST.market)) return "Market";
    if (p.startsWith(PATH_LIST.portfolio)) return "Portfolio";
    if (p.startsWith(PATH_LIST.currencies)) return "Currencies";
    if (p.startsWith(PATH_LIST.discover)) return "Discover";
    if (p.startsWith(PATH_LIST.chat)) return "Chat";
    if (p.startsWith(PATH_LIST.account)) return "Account";

    return "Home";
  }, [location.pathname]);

  const [modalLogOut, setModalLogOut] = useState(false);
  const [modalContacts, setModalContacts] = useState(false);
  const [holidayNotificationDate, setHolidayNotificationDate] = useState<
    string | null
  >(null);
  const {
    kyc,
    kyb,
    analyticsMetadata,
    getAnalyticsMetadata,
    getSubAccounts,
    subAccounts,
  } = useStore(accountStore);

  const {
    logout,
    requestNotificationToken,
    signContractModal,
    setSignContractModal,
    crsDataModal,
    setCrsDataModal,
    inactiveAccountModal,
    setInactiveAccountModal,
    inactiveEmailModal,
    setInactiveEmailModal,
    inactivePinCodeModal,
    setInactivePinCodeModal,
    notVerifiedModal,
    setNotVerifiedModal,
    cantTradeModal,
    setCantTradeModal,
    subAccountSubNumber,
    setSubAccountSubNumber,
    holidayNotificationSeenByDate,
    setHolidayNotificationLocale,
    markHolidayNotificationSeen,
  } = useStore(sessionModel.sessionStore);
  const {
    loadTariffs,
    newAssetDeal,
    updateAssetDeal,
    loadExchanges,
    filterStockIndustries,
    filterSectors,
    filterCountries,
    loadStockFilterOptions,
    checkCurrencyAsset,
    loadFavorites,
    loadAssets,
    loadSpecialOffers,
  } = useStore(marketStore);
  const { loadCurrencies, loadUserAccounts } = useStore(dashboardStore);

  const { connected, connect, disconnect, socket, addRoom, checkChat } =
    useStore(generalSocketStore);

  const { connect: rtConnect, disconnect: rtDisconnect } = useStore(ioRTStore);

  useExchangesTimeFrames();

  useEffect(() => {
    if (!user?.id) {
      setHolidayNotificationDate(null);
      return;
    }

    setHolidayNotificationDate(
      resolveHolidayNotificationDate(holidayNotificationSeenByDate),
    );
  }, [user?.id, holidayNotificationSeenByDate]);

  useEffect(() => {
    if (user?.id) {
      requestNotificationToken();
    }
  }, [user?.id]);

  useEffect(() => {
    rtConnect();

    return () => {
      rtDisconnect();
    };
  }, []);

  useEffect(() => {
    getAnalyticsMetadata();
    loadFavorites();
    filterStockIndustries();
    filterCountries();
    filterSectors();
    loadStockFilterOptions();
    loadCurrencies();
    // loadAllRates();
    loadExchanges();
    loadTariffs();
    loadUserAccounts();
    loadSpecialOffers();
    getSubAccounts();
    checkChat();
    connect();

    return () => {
      disconnect();
    };
  }, []);

  // Сокет авторизуется access-токеном. Если он лежит (например, обвязка
  // восстановления исчерпала попытки), а токен тем временем обновился —
  // поднимаем соединение. Живое соединение не трогаем: auth-колбэк и так
  // подставит свежий токен при следующем переподключении.
  const accessToken = useStore(sessionStore, (s) => s.accessToken);
  useEffect(() => {
    if (!accessToken) return;
    if (!socket.connected) connect();
  }, [accessToken]);

  useEffect(() => {
    if (connected) {
      socket
        .off("chat-new-message")
        .off("new-order")
        .off("update-order")
        .off("update-rates")
        .off("user-accounts");
      socket
        // Портфель и кэш применяются тем же кодом, что и при HTTP-загрузке.
        .on("user-accounts", ({ accounts }) =>
          dashboardStore.getState().applyUserAccounts(accounts),
        )
        .on("chat-new-message", (r) => addRoom(r.room))
        .on("new-order", ({ order }) => newAssetDeal(order))
        .on("update-order", ({ order }) => updateAssetDeal(order))
        .on("update-special-offers", () => loadSpecialOffers())
        .on("update-rates", async () => {
          // loadAllRates();
          await loadAssets({
            type: String(AssetType.Currency),
            page: 0,
            limit: 0,
          });
          checkCurrencyAsset();
        });
    }
  }, [connected]);

  useEffect(() => {
    if (!analyticsMetadata) return;

    Object.entries(analyticsMetadata).forEach(([key, value]) => {
      if (value) {
        firebaseLogEvent(key);
        sircapApi.account.firebaseTrigger({ type: key });
      }
    });
  }, [analyticsMetadata]);

  useEffect(() => {
    if (subAccountSubNumber) {
      const selectedSubAccount = subAccounts.find(
        (sa) => String(sa.id) === subAccountSubNumber,
      );
      if (!selectedSubAccount) {
        setSubAccountSubNumber(null);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    }
  }, [subAccountSubNumber, subAccounts]);

  const handlePageChange = (_pageName: string, path: string) => {
    navigate(path);
  };

  const count = useUnreadMessagesCount("all");

  const renderHeaderItem = () => {
    const pageName = activePage;

    const getUserDefaultImage = () => {
      if (kyc || kyb) {
        if (entity === EntityType.Individual) {
          if (kyc && kyc.gender === "female") {
            return FemaleUser;
          }
        } else {
          return BusinessUser;
        }
      }

      return MaleUser;
    };

    const renderProfileIcon = () => {
      const src = user?.avatar
        ? getCdnUrl(user.avatar, "avatar")
        : getUserDefaultImage();
      const alt = user?.avatar ? "User" : "User icon";

      return (
        <button
          key="profile-icon"
          onClick={() => navigate(PATH_LIST.account)}
          className="layout-home-header-profile-img-btn-wrapper"
        >
          <img className="layout-home-header-profile-img" src={src} alt={alt} />
        </button>
      );
    };

    return (
      <>
        {activePage === "Home" ? (
          <div className="layout-home-header">
            {renderProfileIcon()}

            <img
              className={
                IsVelarApp
                  ? "app-layout-logo-main app-layout-logo-main-velar"
                  : "app-layout-logo-main"
              }
              src={IsVelarApp ? LogoVelar : LogoColumn}
              alt="Logo"
            />

            <div className="app-layout-header-home-buttons">
              {/* //todo hide when there are no unfinished transactions */}
              {/* <button
                onClick={() => navigate(PATH_LIST.unfinishedTransactions)}
                className="app-layout-chat-btn unfinished-transactions-btn"
              >
                <img
                  className="open-chat"
                  src={TransactionsHistoryActive}
                  alt="chat"
                />
              </button> */}

              <button
                onClick={() => navigate(PATH_LIST.chat)}
                className="app-layout-chat-btn"
              >
                <img
                  className="open-chat"
                  src={count ? ChatNewActive : ChatNew}
                  alt="chat"
                />
              </button>
            </div>
          </div>
        ) : activePage === "Discover" ? (
          <p>{t("discover")}</p>
        ) : activePage === "Account" ? (
          <div className="account-nav">
            {/* <p>{t("account")}</p> */}
            <button
              onClick={() => {
                //todo make this work, maybe not
                // if (
                //   previousPathRef.current &&
                //   (previousPathRef.current.includes("account") ||
                //     previousPathRef.current.includes("auth") ||
                //     previousPathRef.current == "/auth/login")
                // ) {
                navigate(PATH_LIST.home);
                // } else {
                //   navigate(-1);
                // }
              }}
            >
              <img src={GoBack} alt="go-back" />
            </button>

            <span onClick={() => setModalLogOut(true)}>{t("logOut")}</span>
          </div>
        ) : activePage === "Chat" ? (
          <div className="account-nav chat-nav">
            <p>{t("chat")}</p>
            <span onClick={() => setModalContacts(true)}>{t("contacts")}</span>
          </div>
        ) : (
          <p>{t(pageName.toLocaleLowerCase())}</p>
        )}
      </>
    );
  };

  const renderNavItem = (
    pageName: string,
    discover: string,
    Icon: SvgIcon,
    path: string,
  ) => {
    const isActive = activePage === pageName;

    return (
      <button
        key={pageName}
        className={
          isActive
            ? "active-page"
            : pageName === "Chat"
            ? "chat__page_container"
            : ""
        }
        onClick={() => handlePageChange(pageName, path)}
      >
        <Icon aria-hidden />
        <span>{discover ? discover : pageName}</span>

        {!isActive && pageName === "Chat" && (
          <NotificationIcon navIcon type="all" />
        )}
      </button>
    );
  };

  const navItems = [
    {
      label: "Home",
      tooltip: "",
      icon: Home,
      path: PATH_LIST.home,
    },
    {
      label: "Market",
      tooltip: "",
      icon: Market,
      path: PATH_LIST.market,
    },
    {
      label: "Portfolio",
      tooltip: "",
      icon: Portfolio,
      path: PATH_LIST.portfolio,
    },
    ...(subAccountSubNumber
      ? []
      : [
          {
            label: "Currencies",
            tooltip: "",
            icon: Currencies,
            path: PATH_LIST.currencies,
          },
        ]),
    {
      label: "Discover",
      tooltip: "Discover",
      icon: Discover,
      path: PATH_LIST.discover,
    },
  ];

  useBlockScrollbar(modalLogOut, [modalLogOut]);

  const onLogout = () => {
    logout();
    setModalLogOut(false);
    document.body.classList.remove("disable-scroll");
    sessionStorage.setItem("logoutState", "true");
    navigate(PATH_LIST.login);
  };

  const { navigateVerification } = useVerificationNavigation();

  const notificationLanguage = getHolidayNotificationLanguage(
    i18n.language || "en",
  );

  useEffect(() => {
    setHolidayNotificationLocale(notificationLanguage);
  }, [notificationLanguage, setHolidayNotificationLocale]);

  const closeHolidayNotification = () => {
    if (!holidayNotificationDate) return;
    markHolidayNotificationSeen(holidayNotificationDate);
    setHolidayNotificationDate(null);
  };

  return (
    <div
      className="app-layout"
      style={{
        paddingTop:
          activePage === "Portfolio" || activePage === "Currencies"
            ? "0px"
            : "68px",
      }}
    >
      <div
        className={
          activePage === "Portfolio" || activePage === "Currencies"
            ? "app-header app-header-hidden"
            : "app-header"
        }
      >
        {renderHeaderItem()}
      </div>

      <Outlet />
      {modalLogOut === true && (
        <Modal
          customModal="modal-log-out"
          onClick={onLogout}
          onClickSecondBtn={() => setModalLogOut(false)}
          onHide={() => setModalLogOut(false)}
          closeIcon={false}
          title={t("logOut")}
          text={t("sureToԼogOut")}
          btnText={t("logOut")}
          btnText2={t("cancel")}
          showSecondBtn={true}
          customOutlet="customOutlet"
        />
      )}
      {modalContacts === true && (
        <ContactsModal onClose={() => setModalContacts(false)} />
      )}
      {holidayNotificationDate && (
        <Modal
          customModal="modal-notification-schedule"
          customOutlet="customOutlet"
          title={t("appRenewalTitle")}
          text={t("appRenewalGreeting")}
          btnText={t("holidayScheduleGotIt")}
          showSecondBtn={false}
          onClick={closeHolidayNotification}
          onHide={closeHolidayNotification}
          customBody={
            <div className="app-layout-holiday-modal">
              <p className="app-layout-holiday-modal__announcement">
                {t("appRenewalP1")}
              </p>
              <p className="app-layout-holiday-modal__announcement">
                {t("appRenewalP2")}
              </p>
              <p className="app-layout-holiday-modal__announcement">
                {t("appRenewalP3")}
              </p>
              <p className="app-layout-holiday-modal__announcement">
                {t("appRenewalP4")}
              </p>

              {/* Услуги временно скрыты (по задаче на 07.08.2026). Код оставлен для будущих праздничных уведомлений. */}
              {false && (
                <>
                  <p className="app-layout-holiday-modal__section-label">
                    {t("holidayScheduleServicesHeading")}
                  </p>
                  <div className="app-layout-holiday-modal__card">
                    {HOLIDAY_SERVICE_ROWS.map((row) => (
                      <div
                        key={row.id}
                        className="app-layout-holiday-modal__row"
                      >
                        <span
                          className={
                            row.available
                              ? "app-layout-holiday-modal__dot app-layout-holiday-modal__dot--ok"
                              : "app-layout-holiday-modal__dot app-layout-holiday-modal__dot--bad"
                          }
                          aria-hidden
                        />
                        <span className="app-layout-holiday-modal__row-label">
                          {t(`holidayScheduleService${row.id}`)}
                        </span>
                        <span
                          className={
                            row.available
                              ? "app-layout-holiday-modal__row-status app-layout-holiday-modal__row-status--ok"
                              : "app-layout-holiday-modal__row-status app-layout-holiday-modal__row-status--bad"
                          }
                        >
                          {row.available
                            ? t("holidayScheduleStatusAvailable")
                            : t("holidayScheduleStatusUnavailable")}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          }
        />
      )}
      {inactiveAccountModal && (
        <Modal
          title={t("accountIsNotActive")}
          text={t("pleaseWaitUntilActivate")}
          btnText={t("ok")}
          onClick={() => setInactiveAccountModal(false)}
          showSecondBtn={false}
          onHide={() => setInactiveAccountModal(false)}
        />
      )}
      {signContractModal && (
        <Modal
          title={t("contractIsNotSigned")}
          text={t("signToMakeTransactions")}
          btnText={t("signContract")}
          onClick={() => {
            navigate(PATH_LIST.signUserContract);
            setSignContractModal(false);
          }}
          showSecondBtn={false}
          onHide={() => setSignContractModal(false)}
          closeIcon={false}
        />
      )}
      {crsDataModal && (
        <Modal
          title={t("crsFormIsNotSigned")}
          text={t("signCrsFormToMakeTransactions")}
          btnText={t("continue")}
          onClick={() => {
            navigate(PATH_LIST.taxInformation);
            setCrsDataModal(false);
          }}
          showSecondBtn={false}
          onHide={() => setCrsDataModal(false)}
          closeIcon={false}
        />
      )}
      {inactiveEmailModal && (
        <Modal
          title={t("emailNotVerify")}
          text={t("pleaseVerifyEmail")}
          btnText={t("confirm")}
          onClick={() => {
            navigate(PATH_LIST.personal);
            setInactiveEmailModal(false);
          }}
          showSecondBtn={false}
          onHide={() => setInactiveEmailModal(false)}
          closeIcon={false}
        />
      )}
      {inactivePinCodeModal && (
        <Modal
          title={t("dontHavePinCode")}
          text={t("pleaseEnterPinCode")}
          btnText={o("setPINCode")}
          onClick={() => {
            navigate(PATH_LIST.pinCode);
            setInactivePinCodeModal(false);
          }}
          showSecondBtn={false}
          onHide={() => setInactivePinCodeModal(false)}
          closeIcon={false}
        />
      )}
      {notVerifiedModal && (
        <Modal
          title={t("notVerifiedAcc")}
          text={t("pleaseVerifyAcc")}
          btnText={t("continue")}
          onClick={() => {
            navigateVerification();
            setNotVerifiedModal(false);
          }}
          showSecondBtn={false}
          onHide={() => setNotVerifiedModal(false)}
          closeIcon={false}
        />
      )}
      {cantTradeModal && (
        <Modal
          title={t("cantTrade")}
          text={t("cantTradeText")}
          btnText={t("ok")}
          onClick={() => setCantTradeModal(false)}
          showSecondBtn={false}
          onHide={() => setCantTradeModal(false)}
          closeIcon={false}
        />
      )}

      {activePage !== "Account" && (
        <nav
          className={
            Capacitor.isNativePlatform()
              ? "app-navbar app-navbar-phone"
              : "app-navbar"
          }
        >
          <div
            style={{
              //todo fix padding part fore mobile
              left: `${
                (100 / navItems.length) *
                navItems.findIndex((item) => item.label === activePage)
              }%`,
              width: `${100 / navItems.length}%`,
              // todo
              //! after cashing open this
              // transition: "left 0s ease",
              // background: "red",
            }}
            className="navbar-tag"
          >
            <span></span>
          </div>

          {navItems.map((item, i) =>
            renderNavItem(item.label, item.tooltip, item.icon, item.path),
          )}
        </nav>
      )}
    </div>
  );
};
